import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, getAuthHeaders } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { ScreenshotUploadField } from "@/components/testing/screenshot-upload-field";

interface AddNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueId: string;
}

export function AddNoteDialog({ open, onOpenChange, issueId }: AddNoteDialogProps) {
  const [noteText, setNoteText] = useState("");
  const [userName, setUserName] = useState<string>(
    localStorage.getItem("issueTrackerUserName") || ""
  );
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const addNoteMutation = useMutation({
    mutationFn: async (data: { note: string; userName: string }) => {
      // Step 1: Create note in activity log
      const response = await apiRequest("POST", `/api/crm/issues/${issueId}/activity/note`, {
        notes: data.note,
        changed_by_name: data.userName,
      });

      const activityLog = await response.json();

      // Step 2: Upload screenshot if present
      if (screenshot) {
        setIsUploadingScreenshot(true);
        try {
          const formData = new FormData();
          formData.append("file", screenshot);
          formData.append("captureType", "manual_upload");
          formData.append("description", "Screenshot attached to note");
          formData.append("metadata", JSON.stringify({
            noteId: activityLog.id,
            activityType: "note_added",
            changedByName: data.userName,
            timestamp: new Date().toISOString(),
          }));

          const authHeaders = await getAuthHeaders();
          const uploadResponse = await fetch(`/api/crm/issues/${issueId}/screenshots/upload`, {
            method: "POST",
            headers: authHeaders,
            credentials: "include",
            body: formData,
          });

          if (!uploadResponse.ok) {
            console.error("Screenshot upload failed, but note was created");
          }
        } catch (error) {
          console.error("Screenshot upload error:", error);
        } finally {
          setIsUploadingScreenshot(false);
        }
      }

      return activityLog;
    },
    onSuccess: (_data) => {
      // Invalidate activity log and screenshots to refresh
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/issues", issueId, "activity"]
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/issues", issueId, "screenshots"]
      });

      // Show success toast
      const message = screenshot
        ? "Your note was added with screenshot attached."
        : "Your note was added to the activity timeline.";

      toast({
        title: "Note added",
        description: message,
      });

      // Reset form
      setNoteText("");
      setScreenshot(null);
      onOpenChange(false);
    },
    onError: (error: Error) => {
      // Show error toast
      toast({
        title: "Failed to add note",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!noteText.trim()) {
      toast({
        title: "Note is required",
        description: "Please enter a note before submitting.",
        variant: "destructive",
      });
      return;
    }

    if (!userName.trim()) {
      toast({
        title: "Name is required",
        description: "Please enter your name.",
        variant: "destructive",
      });
      return;
    }

    // Save user name to localStorage for future use
    localStorage.setItem("issueTrackerUserName", userName.trim());

    // Submit mutation
    addNoteMutation.mutate({
      note: noteText.trim(),
      userName: userName.trim()
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>
              Add a note to track progress, fixes, or verification for this issue.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* User Name Field */}
            <div className="grid gap-2">
              <Label htmlFor="userName">Your Name</Label>
              <Input
                id="userName"
                placeholder="e.g., Dan Peters, Claude AI"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                disabled={addNoteMutation.isPending}
              />
              <p className="text-xs text-muted-foreground">
                Your name will be shown in the activity timeline
              </p>
            </div>

            {/* Note Text Field */}
            <div className="grid gap-2">
              <Label htmlFor="note">Note</Label>
              <Textarea
                id="note"
                placeholder="e.g., Issue found in test SPL-01.2 - search box not visible on mobile. Fixed by adding responsive breakpoints to SearchBar.tsx. Committed in abc123."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={6}
                className="resize-none"
                disabled={addNoteMutation.isPending || isUploadingScreenshot}
              />
              <p className="text-xs text-muted-foreground">
                Describe the issue, fix, or verification details. Include commit hashes if relevant.
              </p>
            </div>

            {/* Screenshot Upload Field */}
            <div className="grid gap-2">
              <ScreenshotUploadField
                value={screenshot}
                onChange={(files) => setScreenshot(Array.isArray(files) ? files[0] ?? null : files)}
                label="Screenshot (Optional)"
                required={false}
                disabled={addNoteMutation.isPending || isUploadingScreenshot}
              />
              <p className="text-xs text-muted-foreground">
                Attach a screenshot to provide visual context for this note.
              </p>
            </div>

            {/* Error Alert */}
            {addNoteMutation.isError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {addNoteMutation.error?.message || "Failed to add note. Please try again."}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={addNoteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!noteText.trim() || !userName.trim() || addNoteMutation.isPending || isUploadingScreenshot}
            >
              {isUploadingScreenshot
                ? "Uploading screenshot..."
                : addNoteMutation.isPending
                ? "Adding note..."
                : "Add Note"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
